import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Container, ListGroup, ProgressBar,
} from 'react-bootstrap';
import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import SetupChecklistCompletedPills from './SetupChecklistCompletedPills';
import SetupChecklistRow from './SetupChecklistRow';
import SetupChecklistContent from './SetupChecklistContent';
import { getSetupChecklistItemDisplay } from './setupChecklistData';
import './setupChecklistStyles.css';
import {
  fetchSubscriptionChecklists,
  selectActiveChecklistType,
  selectPrimaryChecklist,
  selectPrimaryChecklistEntries,
  selectSecondaryChecklist,
  selectSecondaryChecklistEntries,
  selectSubscriptionChecklistLoading,
} from '../../../../redux/slices/subscriptionChecklistSlice/subscriptionChecklistSlice';
import { SubscriptionChecklistType } from '../../../../utility/types/SubscriptionChecklistType';
import {
  selectSessionInformation,
  selectUserAssignmentPreferences,
  setHideChecklistOnMainNav,
} from '../../../../redux/slices/sessionSlice';

function buildViewModelFromEntry(entry, activeTierLevel) {
  const display = getSetupChecklistItemDisplay(entry.itemId);
  return {
    itemId: entry.itemId,
    title: display.title || '',
    complete: Boolean(entry.dateCompleted),
    isUserSettable: Boolean(entry.isUserSettable),
    premium: entry.minTier > activeTierLevel,
    icon: display.icon,
    content: display.content,
    callToAction: display.callToAction,
  };
}

export default function SetupChecklist() {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectSubscriptionChecklistLoading);
  useEffect(() => {
    dispatch(fetchSubscriptionChecklists());
  }, [dispatch]);

  const activeChecklistType = useSelector(selectActiveChecklistType);
  const primaryChecklist = useSelector(selectPrimaryChecklist);
  const secondaryChecklist = useSelector(selectSecondaryChecklist);
  const primaryEntries = useSelector(selectPrimaryChecklistEntries);
  const secondaryEntries = useSelector(selectSecondaryChecklistEntries);
  const { activeSubscription } = useSelector(selectSessionInformation);
  const activeTierLevel = activeSubscription ? activeSubscription.subscriptionTierLevel : 0;
  const userAssignmentPreferences = useSelector(selectUserAssignmentPreferences);
  const isPrimaryChecklistHidden = Boolean(
    userAssignmentPreferences && userAssignmentPreferences.primaryChecklistHidden,
  );

  const isPrimaryActive = activeChecklistType === SubscriptionChecklistType.PRIMARY_CHECKLIST;
  const checklistEntries = isPrimaryActive ? primaryEntries : secondaryEntries;
  const activeChecklistId = isPrimaryActive
    ? (primaryChecklist && primaryChecklist.id)
    : (secondaryChecklist && secondaryChecklist.id);

  const [completedOpenItemId, setCompletedOpenItemId] = useState(null);

  const {
    completedItems, incompleteItems, remaining, percent,
  } = useMemo(() => {
    const viewModels = checklistEntries.map((entry) => buildViewModelFromEntry(
      entry, activeTierLevel,
    ));
    const completed = viewModels.filter((vm) => vm.complete);
    const incomplete = viewModels.filter((vm) => !vm.complete);
    const total = viewModels.length;
    const left = incomplete.length;
    const pct = total === 0 ? 0 : Math.round(((total - left) / total) * 100);
    return {
      completedItems: completed,
      incompleteItems: incomplete,
      remaining: left,
      percent: pct,
    };
  }, [checklistEntries, activeTierLevel]);

  const displayedCompletedItems = useMemo(() => {
    if (isPrimaryActive) return completedItems;
    const primaryCompleted = primaryEntries
      .map((entry) => buildViewModelFromEntry(entry, activeTierLevel))
      .filter((vm) => vm.complete);
    // De-duplicate by itemId in case an item exists in both checklists.
    const seen = new Set();
    return [...primaryCompleted, ...completedItems].filter((vm) => {
      if (seen.has(vm.itemId)) return false;
      seen.add(vm.itemId);
      return true;
    });
  }, [isPrimaryActive, primaryEntries, completedItems, activeTierLevel]);

  const toggle = (id) => setCompletedOpenItemId((prev) => (prev === id ? null : id));

  const buildCallToAction = useCallback((item) => {
    if (typeof item.callToAction !== 'function') return undefined;
    return () => item.callToAction(activeChecklistId, item.itemId, { dispatch });
  }, [activeChecklistId, dispatch]);

  const openCompletedItem = displayedCompletedItems.find((vm) => vm.itemId === completedOpenItemId);
  const showCompletedDetail = Boolean(openCompletedItem);

  if (isLoading) {
    return (
      <div className="row d-flex justify-content-center mt-5">
        <div><ProgressSpinner progressText="Loading..." /></div>
      </div>
    );
  }

  return (
    <Container className="py-4 ra-checklist" style={{ maxWidth: 840 }}>
      <div className="d-flex align-items-center flex-wrap mb-3">
        <h1 className="h2 mb-0 mr-3 font-weight-bold">Guided Setup Checklist</h1>
        <Button
          variant="link"
          className="ml-4 p-0 pt-1"
          onClick={() => dispatch(setHideChecklistOnMainNav({ hidden: !isPrimaryChecklistHidden }))}
        >
          <small>
            <u>{isPrimaryChecklistHidden ? 'show on main nav' : 'hide on main nav'}</u>
          </small>
        </Button>
      </div>

      <div className="d-flex align-items-center mb-3">
        <ProgressBar
          now={percent}
          className="flex-grow-1 mr-3 ra-progress"
        />
        <span className="text-muted small text-nowrap">
          {remaining}
          {' '}
          left
        </span>
      </div>

      <SetupChecklistCompletedPills items={displayedCompletedItems} onToggle={toggle} />

      {showCompletedDetail && (
        <div className="bg-light border-left p-3 mb-3 mt-3 ra-pill-detail">
          <SetupChecklistContent blocks={openCompletedItem.content} />
        </div>
      )}
      <hr className="mt-4 mb-0" />
      <ListGroup variant="flush" as="ul" className="ra-checklist-list">
        {incompleteItems.map((item) => (
          <SetupChecklistRow
            key={item.itemId}
            item={item}
            isOpen={completedOpenItemId === item.itemId}
            onToggle={toggle}
            onCallToAction={buildCallToAction(item)}
          />
        ))}
      </ListGroup>
    </Container>
  );
}
